import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, axiosInstance } from '../App';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import LanguageToggle from '../components/LanguageToggle';

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    password: '',
    rememberMe: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Shown when the user was sent here after a successful registration whose
  // automatic login could not complete — so they know the account exists and
  // simply needs a manual sign in (rather than thinking signup failed).
  const notice = location.state?.notice || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });
      
      const { access_token, role, user_id } = response.data;
      login(access_token, role, user_id, formData.rememberMe);

      // Iter 198 — Honor the originating path so users land back where they
      // started (e.g. /safety-bulletin) instead of being dumped on /discover.
      // The `from` is captured by <RequireAuth> when a protected route boots
      // them out. We only accept relative paths to avoid open-redirect abuse.
      const from = location.state?.from;
      const safeFrom = typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')
        ? from
        : null;

      // Admin always lands on the admin dashboard — `from` is ignored for
      // admins because the only reason they hit /signin is to log in.
      if (role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (safeFrom) {
        // Business users heading to a non-business protected route still get
        // the requested page; the page itself enforces any further role check.
        navigate(safeFrom, { replace: true });
      } else if (role === 'user') {
        navigate('/', { replace: true });
      } else if (role === 'business') {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-600 to-purple-800">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-white hover:text-white/80"
          data-testid="back-button"
        >
          <ArrowLeft className="h-6 w-6 mr-2" />
        </button>
        <LanguageToggle />
      </header>

      {/* Content */}
      <div className="flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="signin-title">
            {t('welcomeBack')}
          </h1>
          <p className="text-gray-600 mb-8">{t('signInSubtitle')}</p>

          {/* Post-registration notice (account created, please sign in) */}
          {notice && (
            <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-sm" data-testid="signin-notice">
              {notice}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="signin-error">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('emailOrUsername')}
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
                data-testid="email-input"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 pe-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center">
              <input
                id="remember"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                data-testid="remember-checkbox"
              />
              <label htmlFor="remember" className="ml-2 text-sm text-gray-700">
                {t('rememberMe')}
              </label>
            </div>

            {/* Forgot password */}
            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-sm text-purple-600 hover:text-purple-700 font-medium" data-testid="forgot-password-link">
                {t('forgotPassword')}
              </Link>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="signin-submit-button"
            >
              {loading ? 'Signing in...' : t('signIn')}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('or')}</span>
              </div>
            </div>

            {/* Forgot password - disabled until flow is built */}
          </form>

          {/* Sign up link */}
          <div className="mt-6 text-center text-sm text-gray-600">
            {t('dontHaveAccount')}{' '}
            <Link to="/signup" className="text-purple-600 hover:text-purple-700 font-semibold">
              {t('signUp')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
